import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';

import { PaymentService } from '../../../api/services/payment.service';
import { BillingService } from '../../../api/services/billing.service';
import { 
  PaymentRequestDto, 
  PaymentMethod, 
  PaymentStatus,
  StripePaymentRequestDto,
  CashPaymentRequestDto,
  CardPaymentRequestDto,
  RefundRequestDto
} from '../../../api/models';
import { ClinicInvoiceDto } from '../../../api/models/clinic-invoice-create-dto';
import { Subject, takeUntil, catchError, of } from 'rxjs';

@Component({
  selector: 'app-payment-test',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatDividerModule,
    MatTooltipModule
  ],
  templateUrl: './payment-test.component.html',
  styleUrls: ['./payment-test.component.scss']
})
export class PaymentTestComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private paymentService = inject(PaymentService);
  private billingService = inject(BillingService);
  private snackBar = inject(MatSnackBar);
  private destroy$ = new Subject<void>();

  // Expose PaymentMethod enum to template
  PaymentMethod = PaymentMethod;

  // Available payment methods for dropdown
  availablePaymentMethods = [
    { value: PaymentMethod.Credits, label: 'Credits' },
    { value: PaymentMethod.SessionBundle, label: 'Session Bundle' },
    { value: PaymentMethod.Membership, label: 'Membership' },
    { value: PaymentMethod.Stripe, label: 'Stripe' },
    { value: PaymentMethod.GoCardless, label: 'GoCardless' },
    { value: PaymentMethod.Cash, label: 'Cash' },
    { value: PaymentMethod.BankTransfer, label: 'Bank Transfer' },
    { value: PaymentMethod.Card, label: 'Card' }
  ];

  // Forms
  generalPaymentForm!: FormGroup;
  stripePaymentForm!: FormGroup;
  cashPaymentForm!: FormGroup;
  cardPaymentForm!: FormGroup;
  paymentLinkForm!: FormGroup;

  // Utility variables
  transactionIdForStatus = '';
  transactionIdForRefund = '';
  refundAmount?: number;
  refundReason = '';

  // State
  isLoading = false;
  lastResponse: any = null;
  
  // Invoice data
  invoice?: ClinicInvoiceDto;
  isLoadingInvoice = false;
  invoiceMode = false; // True when paying for a specific invoice

  ngOnInit(): void {
    this.initializeForms();
    this.checkForInvoicePayment();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkForInvoicePayment(): void {
    this.route.queryParams.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      const invoiceId = params['invoiceId'];
      if (invoiceId) {
        this.invoiceMode = true;
        this.loadInvoiceData(Number(invoiceId));
      }
    });
  }

  private loadInvoiceData(invoiceId: number): void {
    this.isLoadingInvoice = true;
    
    this.billingService.getInvoice(invoiceId).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Error loading invoice:', error);
        this.snackBar.open('Error loading invoice details', 'Close', { 
          duration: 5000,
          panelClass: ['error-snackbar']
        });
        return of(null);
      })
    ).subscribe(invoice => {
      if (invoice) {
        this.invoice = invoice;
        this.updateFormsWithInvoiceData();
      }
      this.isLoadingInvoice = false;
    });
  }

  private updateFormsWithInvoiceData(): void {
    if (!this.invoice) return;

    const invoiceData = {
      invoiceId: this.invoice.clinicInvoiceId,
      patientId: this.invoice.patientId,
      amount: this.getRemainingBalance(),
      currency: 'GBP', // Set to GBP as requested earlier
      description: `Payment for Invoice INV-${this.invoice.clinicInvoiceId.toString().padStart(4, '0')}`
    };

    // Update all forms with invoice data
    this.generalPaymentForm.patchValue(invoiceData);
    this.stripePaymentForm.patchValue(invoiceData);
    this.cashPaymentForm.patchValue(invoiceData);
    this.cardPaymentForm.patchValue(invoiceData);
    this.paymentLinkForm.patchValue(invoiceData);
  }

  getRemainingBalance(): number {
    if (!this.invoice) return 0;
    return this.invoice.total - this.invoice.amountPaid - this.invoice.creditsUsed;
  }

  private initializeForms(): void {
    this.generalPaymentForm = this.fb.group({
      invoiceId: [1, [Validators.required]],
      patientId: [123, [Validators.required]],
      appointmentId: [456],
      amount: [100.00, [Validators.required, Validators.min(0.01)]],
      currency: ['USD', [Validators.required]],
      description: ['Test payment', [Validators.required]],
      paymentMethod: [PaymentMethod.Stripe, [Validators.required]],
      stripePaymentMethodId: ['pm_card_visa']
    });

    this.stripePaymentForm = this.fb.group({
      invoiceId: [1, [Validators.required]],
      patientId: [123, [Validators.required]],
      appointmentId: [456],
      amount: [150.00, [Validators.required, Validators.min(0.01)]],
      currency: ['USD'],
      description: ['Stripe test payment'],
      paymentMethodId: ['pm_card_visa', [Validators.required]],
      customerId: ['cus_test123'],
      savePaymentMethod: [true]
    });

    this.cashPaymentForm = this.fb.group({
      invoiceId: [1, [Validators.required]],
      patientId: [123, [Validators.required]],
      appointmentId: [456],
      amount: [75.00, [Validators.required, Validators.min(0.01)]],
      currency: ['USD'],
      description: ['Cash test payment'],
      receivedBy: ['Reception Desk', [Validators.required]],
      receiptNumber: ['CASH-001-2024'],
      changeGiven: [0]
    });

    this.cardPaymentForm = this.fb.group({
      invoiceId: [1, [Validators.required]],
      patientId: [123, [Validators.required]],
      appointmentId: [456],
      amount: [200.00, [Validators.required, Validators.min(0.01)]],
      currency: ['USD'],
      description: ['Card test payment'],
      cardNumber: ['4111111111111111', [Validators.required]],
      cardHolderName: ['John Doe', [Validators.required]],
      expiryMonth: [12, [Validators.required, Validators.min(1), Validators.max(12)]],
      expiryYear: [2025, [Validators.required]],
      cvv: ['123', [Validators.required]]
    });

    this.paymentLinkForm = this.fb.group({
      invoiceId: [1, [Validators.required]],
      patientId: [123, [Validators.required]],
      amount: [120.00, [Validators.required, Validators.min(0.01)]],
      currency: ['USD'],
      description: ['Payment link test'],
      returnUrl: ['https://yourclinic.com/payment/success'],
      cancelUrl: ['https://yourclinic.com/payment/cancel']
    });
  }

  processGeneralPayment(): void {
    if (this.generalPaymentForm.invalid) return;

    this.isLoading = true;
    const formValue = this.generalPaymentForm.value;
    
    const paymentRequest: PaymentRequestDto = {
      invoiceId: formValue.invoiceId,
      patientId: formValue.patientId,
      appointmentId: formValue.appointmentId,
      amount: formValue.amount,
      paymentMethod: formValue.paymentMethod,
      notes: formValue.description,
      paymentDetails: {
        stripePaymentMethodId: formValue.stripePaymentMethodId
      }
    };

    this.paymentService.apiPaymentProcessPost$Json({ body: paymentRequest })
      .subscribe({
        next: (response) => {
          this.handleResponse(response, 'General payment processed successfully');
        },
        error: (error) => {
          this.handleError(error, 'Failed to process general payment');
        }
      });
  }

  processStripePayment(): void {
    if (this.stripePaymentForm.invalid) return;

    this.isLoading = true;
    const formValue = this.stripePaymentForm.value;
    
    const stripeRequest: StripePaymentRequestDto = {
      invoiceId: formValue.invoiceId,
      patientId: formValue.patientId,
      amount: formValue.amount,
      paymentMethodId: formValue.paymentMethodId,
      customerId: formValue.customerId,
      description: formValue.description,
      savePaymentMethod: formValue.savePaymentMethod
    };

    this.paymentService.apiPaymentStripePost$Json({ body: stripeRequest })
      .subscribe({
        next: (response) => {
          this.handleResponse(response, 'Stripe payment processed successfully');
        },
        error: (error) => {
          this.handleError(error, 'Failed to process Stripe payment');
        }
      });
  }

  processCashPayment(): void {
    if (this.cashPaymentForm.invalid) return;

    this.isLoading = true;
    const formValue = this.cashPaymentForm.value;
    
    const cashRequest: CashPaymentRequestDto = {
      invoiceId: formValue.invoiceId,
      patientId: formValue.patientId,
      appointmentId: formValue.appointmentId,
      amount: formValue.amount,
      currency: formValue.currency,
      description: formValue.description,
      receivedBy: formValue.receivedBy,
      receiptNumber: formValue.receiptNumber,
      changeGiven: formValue.changeGiven
    };

    this.paymentService.apiPaymentCashPost$Json({ body: cashRequest })
      .subscribe({
        next: (response) => {
          this.handleResponse(response, 'Cash payment recorded successfully');
        },
        error: (error) => {
          this.handleError(error, 'Failed to record cash payment');
        }
      });
  }

  processCardPayment(): void {
    if (this.cardPaymentForm.invalid) return;

    this.isLoading = true;
    const formValue = this.cardPaymentForm.value;
    
    const cardRequest: CardPaymentRequestDto = {
      invoiceId: formValue.invoiceId,
      patientId: formValue.patientId,
      appointmentId: formValue.appointmentId,
      amount: formValue.amount,
      currency: formValue.currency,
      description: formValue.description,
      cardNumber: formValue.cardNumber,
      cardHolderName: formValue.cardHolderName,
      expiryMonth: formValue.expiryMonth,
      expiryYear: formValue.expiryYear,
      cvv: formValue.cvv
    };

    this.paymentService.apiPaymentCardPost$Json({ body: cardRequest })
      .subscribe({
        next: (response) => {
          this.handleResponse(response, 'Card payment processed successfully');
        },
        error: (error) => {
          this.handleError(error, 'Failed to process card payment');
        }
      });
  }

  createPaymentLink(): void {
    if (this.paymentLinkForm.invalid) return;

    this.isLoading = true;
    const formValue = this.paymentLinkForm.value;
    
    const linkRequest: PaymentRequestDto = {
      invoiceId: formValue.invoiceId,
      patientId: formValue.patientId,
      amount: formValue.amount,
      paymentMethod: PaymentMethod.Stripe,
      notes: formValue.description,
      paymentDetails: {
        returnUrl: formValue.returnUrl,
        cancelUrl: formValue.cancelUrl
      }
    };

    this.paymentService.apiPaymentLinkPost$Json({ body: linkRequest })
      .subscribe({
        next: (response) => {
          this.handleResponse(response, 'Payment link created successfully');
        },
        error: (error) => {
          this.handleError(error, 'Failed to create payment link');
        }
      });
  }

  checkPaymentStatus(): void {
    if (!this.transactionIdForStatus) return;

    this.isLoading = true;
    this.paymentService.apiPaymentStatusTransactionIdGet$Json({ 
      transactionId: this.transactionIdForStatus 
    }).subscribe({
      next: (response) => {
        this.handleResponse(response, 'Payment status retrieved successfully');
      },
      error: (error) => {
        this.handleError(error, 'Failed to check payment status');
      }
    });
  }

  processRefund(): void {
    if (!this.transactionIdForRefund) return;

    this.isLoading = true;
    const refundRequest: RefundRequestDto = {
      transactionId: this.transactionIdForRefund,
      amount: this.refundAmount,
      reason: this.refundReason || 'Test refund'
    };

    this.paymentService.apiPaymentRefundTransactionIdPost$Json({
      transactionId: this.transactionIdForRefund,
      body: refundRequest
    }).subscribe({
      next: (response) => {
        this.handleResponse(response, 'Refund processed successfully');
      },
      error: (error) => {
        this.handleError(error, 'Failed to process refund');
      }
    });
  }

  private handleResponse(response: any, successMessage: string): void {
    this.isLoading = false;
    this.lastResponse = response;
    
    if (response.success) {
      this.snackBar.open(successMessage, 'Close', {
        duration: 5000,
        panelClass: ['success-snackbar']
      });
      
      // If we're in invoice mode and payment was successful, navigate back to invoice
      if (this.invoiceMode && this.invoice) {
        setTimeout(() => {
          this.router.navigate(['/clinic/billing/invoice', this.invoice!.clinicInvoiceId]);
        }, 2000); // Give time to see the success message
      }
    } else {
      this.snackBar.open(response.errorMessage || 'Operation failed', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
    }
  }

  private handleError(error: any, errorMessage: string): void {
    this.isLoading = false;
    this.lastResponse = error;
    
    console.error('Payment operation failed:', error);
    this.snackBar.open(errorMessage, 'Close', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }
} 